'use client';

import { RICH_TEXT_CLASS, sharedConverters } from '@/components/RichTextConverters/converters';
import { RichText } from '@payloadcms/richtext-lexical/react';
import { SearchFilter, SearchIcon, useConfig } from '@payloadcms/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { formatDate } from './format-date';
import { headingAnchors } from './headings';
import { expandedOnArrival, expandMatchingSections, toggleSection, withSectionExpanded } from './sections';

import type { JSXConvertersFunction } from '@payloadcms/richtext-lexical/react';
import type { DocumentationHeading } from './headings';

const BASE_CLASS = 'fv-documentation';

interface DocumentationItem {
  id: string;
  slug: string;
  section: string;
  title: string;
  summary: string;
  updatedAt: string;
  headings: DocumentationHeading[];
  haystack: string;
  body: unknown;
}

interface Props {
  initialSlug?: string;
  pages: DocumentationItem[];
  sections: { label: string; value: string }[];
}

const COPY_FEEDBACK_MS = 1500;
const TARGET_FEEDBACK_MS = 1400;
const TARGETED = 'is-targeted';

const useReaderRoute = (): string => {
  const {
    config: {
      routes: { admin: adminRoute },
    },
  } = useConfig();

  return `${adminRoute}/documentation`;
};

const HeadingLink = ({ children, id, slug }: { children: React.ReactNode; id: string; slug: string }) => {
  const [copied, setCopied] = useState(false);
  const readerRoute = useReaderRoute();

  useEffect(() => {
    if (!copied) return;

    const timer = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);

    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${readerRoute}/${slug}#${id}`);
      setCopied(true);
    } catch {
      // the clipboard is unavailable in this context
    }
  };

  return (
    <>
      <a className={`${BASE_CLASS}__anchor`} href={`#${id}`}>
        {children}
      </a>
      <button
        type="button"
        className={`${BASE_CLASS}__copy${copied ? ' is-copied' : ''}`}
        onClick={copy}
        aria-label={copied ? 'Link copied' : 'Copy a link to this section'}
        title={copied ? 'Link copied' : 'Copy a link to this section'}
      >
        {copied ? (
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M3.5 8.5 6.5 11.5 12.5 5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M6.6 9.4a2.6 2.6 0 0 0 3.7 0l2.1-2.1a2.6 2.6 0 0 0-3.7-3.7l-.6.6M9.4 6.6a2.6 2.6 0 0 0-3.7 0L3.6 8.7a2.6 2.6 0 0 0 3.7 3.7l.6-.6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        )}
      </button>
    </>
  );
};

/** Headings render with the same anchor `collectHeadings` produced, so the sidebar links resolve. */
export const anchoredHeadings =
  (anchors: Map<unknown, string>, slug: string): JSXConvertersFunction =>
  ({ defaultConverters }) => ({
    ...sharedConverters({ defaultConverters }),
    heading: ({ node, nodesToJSX }) => {
      const children = nodesToJSX({ nodes: node.children });
      const Tag = node.tag;
      const id = anchors.get(node);

      if (Tag !== 'h2' || !id) return <Tag>{children}</Tag>;

      return (
        <Tag className={`${BASE_CLASS}__heading`} id={id}>
          <HeadingLink id={id} slug={slug}>
            {children}
          </HeadingLink>
        </Tag>
      );
    },
  });

export const DocumentationClient = ({ initialSlug, pages, sections }: Props) => {
  const readerRoute = useReaderRoute();
  const [query, setQuery] = useState('');
  const [openSlug, setOpenSlug] = useState(initialSlug ?? pages[0]?.slug);
  const [expanded, setExpanded] = useState(() =>
    expandedOnArrival(
      pages,
      sections.map(({ value }) => value),
      initialSlug,
    ),
  );

  const targetTimer = useRef(0);
  const articleRef = useRef<HTMLElement>(null);

  const needle = query.trim().toLowerCase();

  useEffect(
    () => () => {
      window.clearTimeout(targetTimer.current);
      for (const node of document.querySelectorAll(`.${TARGETED}`)) {
        node.classList.remove(TARGETED);
      }
    },
    [openSlug],
  );

  const search = useCallback(
    (value: string) => {
      setQuery(value);
      setExpanded((current) => expandMatchingSections(current, pages, value.trim().toLowerCase()));
    },
    [pages],
  );

  const matches = useMemo(() => (needle ? pages.filter((page) => page.haystack.includes(needle)) : pages), [needle, pages]);

  const open = matches.find((page) => page.slug === openSlug) ?? matches[0] ?? pages[0];

  const converters = useMemo(() => anchoredHeadings(headingAnchors(open?.body), open?.slug ?? ''), [open?.body, open?.slug]);

  /** A shared link carries a hash, and the body it points at only exists once this has rendered. */
  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    if (!anchor) return;

    const target = document.getElementById(anchor);
    target?.scrollIntoView({ behavior: 'smooth' });
  }, [open?.slug]);

  const markTargeted = (id: string) => {
    const target = document.getElementById(id);
    if (target === null || articleRef.current?.contains(target) !== true) return;

    window.clearTimeout(targetTimer.current);
    for (const node of document.querySelectorAll(`.${TARGETED}`)) {
      node.classList.remove(TARGETED);
    }

    target.classList.add(TARGETED);
    targetTimer.current = window.setTimeout(() => target.classList.remove(TARGETED), TARGET_FEEDBACK_MS);
  };

  const select = (slug: string) => {
    setOpenSlug(slug);
    setExpanded((current) => withSectionExpanded(current, pages.find((page) => page.slug === slug)?.section));
    window.history.replaceState(null, '', `${readerRoute}/${slug}`);
  };

  if (!pages.length) {
    return (
      <div className={`${BASE_CLASS}__empty`}>
        <h1>Documentation</h1>
        <p>No documentation page has been published yet.</p>
      </div>
    );
  }

  return (
    <div className={BASE_CLASS}>
      <nav className={`${BASE_CLASS}__nav`} aria-label="Documentation">
        <div className={`${BASE_CLASS}__nav-head`}>
          <div className={`${BASE_CLASS}__search`}>
            <SearchIcon />
            <SearchFilter label="Search the documentation" handleChange={search} />
          </div>

          <p className={`${BASE_CLASS}__hits`}>{needle ? `${matches.length} of ${pages.length} pages` : `${pages.length} pages`}</p>
        </div>

        <div className={`${BASE_CLASS}__nav-body`}>
          {!matches.length && <p className={`${BASE_CLASS}__none`}>No page matches “{query.trim()}”.</p>}

          {sections.map((section, index) => {
            const inSection = matches.filter((page) => page.section === section.value);
            if (!inSection.length) return null;

            const listId = `${BASE_CLASS}-section-${index}`;
            const isExpanded = expanded.has(section.value);

            return (
              <section className={`${BASE_CLASS}__section`} key={section.value}>
                <h2 className={`${BASE_CLASS}__section-heading`}>
                  <button
                    type="button"
                    className={`${BASE_CLASS}__section-label${isExpanded ? ' is-expanded' : ''}`}
                    onClick={() => setExpanded((current) => toggleSection(current, section.value))}
                    aria-expanded={isExpanded}
                    aria-controls={listId}
                    aria-label={`${section.label}, ${inSection.length} ${inSection.length === 1 ? 'page' : 'pages'}`}
                  >
                    <svg className={`${BASE_CLASS}__chevron`} width="10" height="10" viewBox="0 0 16 16" aria-hidden="true">
                      <path
                        d="m6 3.5 5 4.5-5 4.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{section.label}</span>
                    <span className={`${BASE_CLASS}__section-count`} aria-hidden="true">
                      {inSection.length}
                    </span>
                  </button>
                </h2>

                <ul className={`${BASE_CLASS}__pages`} id={listId} hidden={!isExpanded}>
                  {inSection.map((page) => (
                    <li key={page.id}>
                      <button
                        type="button"
                        className={`${BASE_CLASS}__page${page.slug === open?.slug ? ' is-open' : ''}`}
                        onClick={() => select(page.slug)}
                        {...(page.slug === open?.slug && { 'aria-current': 'page' as const })}
                      >
                        {page.title}
                      </button>
                      {page.slug === open?.slug && page.headings.length > 1 && (
                        <ul className={`${BASE_CLASS}__headings`}>
                          {page.headings.map((heading) => (
                            <li key={heading.id}>
                              <a href={`#${heading.id}`} onClick={() => markTargeted(heading.id)}>
                                {heading.text}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </nav>

      {open && (
        <article className={`${BASE_CLASS}__reader`} ref={articleRef}>
          <p className={`${BASE_CLASS}__kicker`}>{sections.find((section) => section.value === open.section)?.label ?? open.section}</p>
          <h1 className={`${BASE_CLASS}__title`}>{open.title}</h1>
          <p className={`${BASE_CLASS}__stamp`}>Updated {formatDate(open.updatedAt)}</p>
          <p className={`${BASE_CLASS}__summary`}>{open.summary}</p>
          <RichText className={`${BASE_CLASS}__body ${RICH_TEXT_CLASS}`} converters={converters} data={open.body as never} />
        </article>
      )}
    </div>
  );
};
